import React, { useState, useEffect } from 'react';
import {
  Play,
  Loader2,
  CheckCircle,
  XCircle,
  Download,
  Filter,
  ChevronDown,
  ChevronRight,
  User,
  Settings,
  Save,
  RotateCcw,
  Edit3,
} from 'lucide-react';
import axios from 'axios';

// Get API base URL from environment or default
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

// ============================================================================
// Types
// ============================================================================

type RoleType =
  | 'PLATFORM_ADMIN' | 'PLATFORM_SUPPORT' | 'MASTER_ADMIN'
  | 'PROVIDER_OWNER' | 'PROVIDER_ADMIN' | 'PROVIDER_USER' | 'SUPER_ADMIN' | 'SERVICE_ADMIN' | 'SERVICE_USER'
  | 'BUSINESS_OWNER' | 'BUSINESS_ADMIN' | 'BUSINESS_USER' | 'BUSINESS_AGENT' | 'OPERATOR_ADMIN' | 'OPERATOR_USER';

interface TestUser {
  username: string;
  password: string;
  role: RoleType;
  description: string;
}

interface EndpointTest {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  description: string;
  allowedRoles: RoleType[];
  queryParams?: Record<string, string>;
  body?: any;
}

interface TestResult {
  endpoint: string;
  method: string;
  role: string;
  expectedAccess: boolean;
  actualStatus: number;
  passed: boolean;
  responseTime: number;
  error?: string;
}

interface CategoryResults {
  category: string;
  results: TestResult[];
  passed: number;
  failed: number;
}

// ============================================================================
// Test Configuration
// ============================================================================

const DEFAULT_TEST_USERS: Record<RoleType, TestUser> = {
  PLATFORM_ADMIN: { username: 'platform_admin@teeoff.com', password: 'Test@123', role: 'PLATFORM_ADMIN', description: 'Platform Admin' },
  PLATFORM_SUPPORT: { username: 'platform_support@teeoff.com', password: 'Test@123', role: 'PLATFORM_SUPPORT', description: 'Platform Support' },
  MASTER_ADMIN: { username: 'master_admin@teeoff.com', password: 'Test@123', role: 'MASTER_ADMIN', description: 'Master Admin (Legacy)' },
  PROVIDER_OWNER: { username: 'provider_owner@tvoice.com', password: 'Test@123', role: 'PROVIDER_OWNER', description: 'Provider Owner' },
  PROVIDER_ADMIN: { username: 'provider_admin@tvoice.com', password: 'Test@123', role: 'PROVIDER_ADMIN', description: 'Provider Admin' },
  PROVIDER_USER: { username: 'provider_user@tvoice.com', password: 'Test@123', role: 'PROVIDER_USER', description: 'Provider User' },
  SUPER_ADMIN: { username: 'super_admin@tvoice.com', password: 'Test@123', role: 'SUPER_ADMIN', description: 'Super Admin (Legacy)' },
  SERVICE_ADMIN: { username: 'service_admin@tvoice.com', password: 'Test@123', role: 'SERVICE_ADMIN', description: 'Service Admin (Legacy)' },
  SERVICE_USER: { username: 'service_user@tvoice.com', password: 'Test@123', role: 'SERVICE_USER', description: 'Service User (Legacy)' },
  BUSINESS_OWNER: { username: 'business_owner@econet.com', password: 'Test@123', role: 'BUSINESS_OWNER', description: 'Business Owner' },
  BUSINESS_ADMIN: { username: 'business_admin@econet.com', password: 'Test@123', role: 'BUSINESS_ADMIN', description: 'Business Admin' },
  BUSINESS_USER: { username: 'business_user@econet.com', password: 'Test@123', role: 'BUSINESS_USER', description: 'Business User' },
  BUSINESS_AGENT: { username: 'business_agent@econet.com', password: 'Test@123', role: 'BUSINESS_AGENT', description: 'Business Agent' },
  OPERATOR_ADMIN: { username: 'operator_admin@econet.com', password: 'Test@123', role: 'OPERATOR_ADMIN', description: 'Operator Admin (Legacy)' },
  OPERATOR_USER: { username: 'operator_user@econet.com', password: 'Test@123', role: 'OPERATOR_USER', description: 'Operator User (Legacy)' },
};

// LocalStorage key for persisted credentials
const STORAGE_KEY = 'api-tester-credentials';

const ALL_ROLES = Object.keys(DEFAULT_TEST_USERS) as RoleType[];

const QUICK_TEST_ROLES: RoleType[] = ['PLATFORM_ADMIN', 'PROVIDER_OWNER', 'BUSINESS_ADMIN', 'BUSINESS_USER', 'BUSINESS_AGENT'];

// All authenticated users
const ALL_AUTHENTICATED = ALL_ROLES;
const PLATFORM_ADMINS: RoleType[] = ['PLATFORM_ADMIN', 'MASTER_ADMIN'];
const PROVIDER_ADMINS: RoleType[] = ['PROVIDER_OWNER', 'PROVIDER_ADMIN', 'SUPER_ADMIN', 'SERVICE_ADMIN'];
const BUSINESS_ADMINS: RoleType[] = ['BUSINESS_OWNER', 'BUSINESS_ADMIN', 'OPERATOR_ADMIN'];

// ============================================================================
// Test Definitions
// ============================================================================

// Provider level users (includes legacy)
const PROVIDER_LEVEL: RoleType[] = ['PROVIDER_OWNER', 'PROVIDER_ADMIN', 'PROVIDER_USER', 'SUPER_ADMIN', 'SERVICE_ADMIN', 'SERVICE_USER'];
// Business level users (includes legacy)
const BUSINESS_LEVEL: RoleType[] = ['BUSINESS_OWNER', 'BUSINESS_ADMIN', 'BUSINESS_USER', 'BUSINESS_AGENT', 'OPERATOR_ADMIN', 'OPERATOR_USER'];

const TEST_CATEGORIES: { category: string; tests: EndpointTest[] }[] = [
  {
    category: 'Users',
    tests: [
      { method: 'GET', path: '/users/me', description: 'Get current user', allowedRoles: ALL_AUTHENTICATED },
      { method: 'GET', path: '/users', description: 'List users', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS], queryParams: { page: '0', size: '10' } },
    ]
  },
  {
    category: 'Organizations',
    tests: [
      { method: 'GET', path: '/organizations', description: 'List organizations', allowedRoles: PLATFORM_ADMINS },
    ]
  },
  {
    category: 'Contacts',
    tests: [
      { method: 'GET', path: '/contacts', description: 'List contacts', allowedRoles: ALL_AUTHENTICATED, queryParams: { page: '0', size: '10' } },
      { method: 'POST', path: '/contacts', description: 'Create contact', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS, 'BUSINESS_USER', 'OPERATOR_USER', 'PLATFORM_SUPPORT'], body: { name: 'Test API', phoneNumber: '+1234567890', email: 'test@example.com' } },
      { method: 'GET', path: '/contacts/search', description: 'Search contacts', allowedRoles: ALL_AUTHENTICATED, queryParams: { q: 'test' } },
      { method: 'GET', path: '/contacts/stats', description: 'Contact stats', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS, 'PLATFORM_SUPPORT'] },
    ]
  },
  {
    category: 'Call Logs',
    tests: [
      { method: 'GET', path: '/calls', description: 'List call logs', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS], queryParams: { page: '0', size: '10' } },
    ]
  },
  {
    category: 'Audio Sessions',
    tests: [
      { method: 'GET', path: '/audio-sessions', description: 'List audio sessions', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS], queryParams: { page: '0', size: '10' } },
    ]
  },
  {
    category: 'Transfers',
    tests: [
      { method: 'GET', path: '/transfers', description: 'List transfers', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS], queryParams: { page: '0', size: '10' } },
    ]
  },
  {
    category: 'IVR',
    tests: [
      { method: 'GET', path: '/ivr/flows', description: 'List IVR flows', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS], queryParams: { page: '0', size: '10' } },
    ]
  },
  {
    category: 'WhatsApp',
    tests: [
      { method: 'GET', path: '/whatsapp/campaigns', description: 'List campaigns', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_LEVEL, ...BUSINESS_LEVEL], queryParams: { page: '0', size: '10' } },
      { method: 'GET', path: '/whatsapp/messages', description: 'List messages', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_LEVEL, ...BUSINESS_LEVEL], queryParams: { page: '0', size: '10' } },
      { method: 'GET', path: '/whatsapp/templates', description: 'List templates', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_LEVEL, ...BUSINESS_LEVEL] },
    ]
  },
  {
    category: 'SMS',
    tests: [
      { method: 'GET', path: '/sms/messages', description: 'List SMS messages', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS], queryParams: { page: '0', size: '10' } },
      { method: 'GET', path: '/sms/campaigns', description: 'List SMS campaigns', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS], queryParams: { page: '0', size: '10' } },
    ]
  },
  {
    category: 'RCS',
    tests: [
      { method: 'GET', path: '/rcs/agents', description: 'List RCS agents', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS] },
      { method: 'GET', path: '/rcs/messages', description: 'List RCS messages', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS], queryParams: { page: '0', size: '10' } },
    ]
  },
  {
    category: 'Billing',
    tests: [
      { method: 'GET', path: '/billing/accounts', description: 'Billing accounts', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS] },
      { method: 'GET', path: '/billing/rates', description: 'Service rates', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS] },
      { method: 'GET', path: '/billing/invoices', description: 'List invoices', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS], queryParams: { page: '0', size: '10' } },
      { method: 'GET', path: '/billing/stats', description: 'Billing stats', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS] },
    ]
  },
  {
    category: 'Developer',
    tests: [
      { method: 'GET', path: '/developer/api-keys', description: 'List API keys', allowedRoles: ALL_AUTHENTICATED },
      { method: 'GET', path: '/developer/webhooks', description: 'List webhooks', allowedRoles: ALL_AUTHENTICATED },
    ]
  },
  {
    category: 'Settings',
    tests: [
      { method: 'GET', path: '/settings/preferences', description: 'User preferences', allowedRoles: ALL_AUTHENTICATED },
      { method: 'GET', path: '/settings/security', description: 'Security settings', allowedRoles: ALL_AUTHENTICATED },
      { method: 'GET', path: '/settings/security/sessions', description: 'Active sessions', allowedRoles: ALL_AUTHENTICATED },
    ]
  },
  {
    category: 'Notifications',
    tests: [
      { method: 'GET', path: '/notifications', description: 'List notifications', allowedRoles: ALL_AUTHENTICATED },
      { method: 'GET', path: '/notifications/stats', description: 'Notification stats', allowedRoles: ALL_AUTHENTICATED },
    ]
  },
  {
    category: 'Support',
    tests: [
      { method: 'GET', path: '/support/tickets', description: 'List tickets', allowedRoles: ALL_AUTHENTICATED },
      { method: 'GET', path: '/support/faq/categories', description: 'FAQ categories', allowedRoles: ALL_AUTHENTICATED },
    ]
  },
  {
    category: 'Analytics',
    tests: [
      { method: 'GET', path: '/analytics/overview', description: 'Analytics overview', allowedRoles: ALL_AUTHENTICATED },
      { method: 'GET', path: '/analytics/channel', description: 'Channel analytics', allowedRoles: ALL_AUTHENTICATED },
      { method: 'GET', path: '/analytics/campaigns', description: 'Campaign analytics', allowedRoles: ALL_AUTHENTICATED },
    ]
  },
  {
    category: 'Reports',
    tests: [
      { method: 'GET', path: '/reports/call-summary', description: 'Call summary', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS] },
      { method: 'GET', path: '/reports/channel-usage', description: 'Channel usage', allowedRoles: [...PLATFORM_ADMINS, ...PROVIDER_ADMINS, ...BUSINESS_ADMINS] },
      { method: 'GET', path: '/reports/revenue', description: 'Revenue report', allowedRoles: PLATFORM_ADMINS },
    ]
  },
  {
    category: 'Audit',
    tests: [
      { method: 'GET', path: '/audit/logs', description: 'Audit logs', allowedRoles: ALL_AUTHENTICATED, queryParams: { page: '0', size: '10' } },
    ]
  },
  {
    category: 'Diagnostics',
    tests: [
      { method: 'GET', path: '/admin/diagnostics/tenant-info', description: 'Tenant info', allowedRoles: PLATFORM_ADMINS },
      { method: 'GET', path: '/admin/diagnostics/organizations', description: 'Org diagnostics', allowedRoles: PLATFORM_ADMINS },
    ]
  },
];

// ============================================================================
// Component
// ============================================================================

interface LoginError {
  role: RoleType;
  username: string;
  error: string;
}

const ApiTesterPage: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentTest: '' });
  const [results, setResults] = useState<CategoryResults[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [selectedRoles, setSelectedRoles] = useState<Set<RoleType>>(new Set(QUICK_TEST_ROLES));
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set(TEST_CATEGORIES.map(c => c.category)));
  const [tokenCache, setTokenCache] = useState<Map<RoleType, string>>(new Map());
  const [loginErrors, setLoginErrors] = useState<LoginError[]>([]);
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [showCategorySelector, setShowCategorySelector] = useState(false);

  // Editable test users configuration
  const [testUsers, setTestUsers] = useState<Record<RoleType, TestUser>>(DEFAULT_TEST_USERS);
  const [showCredentialsPanel, setShowCredentialsPanel] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleType | null>(null);
  const [editForm, setEditForm] = useState({ username: '', password: '' });

  // Load saved credentials from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setTestUsers(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        console.error('Failed to load saved credentials');
      }
    }
  }, []);

  // Save credentials to localStorage
  const saveCredentials = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(testUsers));
  };

  // Reset credentials to defaults
  const resetCredentials = () => {
    setTestUsers(DEFAULT_TEST_USERS);
    localStorage.removeItem(STORAGE_KEY);
  };

  // Update a single user's credentials
  const updateUserCredentials = (role: RoleType, username: string, password: string) => {
    setTestUsers(prev => ({
      ...prev,
      [role]: { ...prev[role], username, password }
    }));
  };

  // Start editing a role
  const startEditing = (role: RoleType) => {
    setEditingRole(role);
    setEditForm({ username: testUsers[role].username, password: testUsers[role].password });
  };

  // Save editing
  const saveEditing = () => {
    if (editingRole) {
      updateUserCredentials(editingRole, editForm.username, editForm.password);
      setEditingRole(null);
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingRole(null);
    setEditForm({ username: '', password: '' });
  };

  // Login and get token for a role
  const loginUser = async (role: RoleType): Promise<string> => {
    if (tokenCache.has(role)) {
      return tokenCache.get(role)!;
    }

    const user = testUsers[role];
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username: user.username,
        password: user.password
      }, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000
      });

      // Backend returns: { success: boolean, data: { user, token } }
      const token = response.data.data?.token || response.data.token || response.data.accessToken;
      if (token) {
        setTokenCache(prev => new Map(prev).set(role, token));
        return token;
      }
      const noTokenError = 'No token in response - check if user exists';
      setLoginErrors(prev => {
        if (!prev.find(e => e.role === role)) {
          return [...prev, { role, username: user.username, error: noTokenError }];
        }
        return prev;
      });
      throw new Error(noTokenError);
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;
      // Track login error
      setLoginErrors(prev => {
        if (!prev.find(e => e.role === role)) {
          return [...prev, { role, username: user.username, error: errorMsg }];
        }
        return prev;
      });
      throw new Error(`Login failed: ${errorMsg}`);
    }
  };

  // Test single endpoint with a role
  const testEndpoint = async (test: EndpointTest, role: RoleType): Promise<TestResult> => {
    const startTime = performance.now();
    const expectSuccess = test.allowedRoles.includes(role);

    try {
      const token = await loginUser(role);

      const config: any = {
        method: test.method,
        url: `${API_BASE_URL}${test.path}`,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        params: test.queryParams,
        validateStatus: () => true
      };

      if (test.body && ['POST', 'PUT', 'PATCH'].includes(test.method)) {
        config.data = test.body;
      }

      const response = await axios(config);
      const responseTime = Math.round(performance.now() - startTime);

      const passed = expectSuccess
        ? response.status >= 200 && response.status < 300
        : response.status === 403 || response.status === 401;

      return {
        endpoint: `${test.method} ${test.path}`,
        method: test.method,
        role,
        expectedAccess: expectSuccess,
        actualStatus: response.status,
        passed,
        responseTime
      };
    } catch (error: any) {
      return {
        endpoint: `${test.method} ${test.path}`,
        method: test.method,
        role,
        expectedAccess: expectSuccess,
        actualStatus: 0,
        passed: false,
        responseTime: Math.round(performance.now() - startTime),
        error: error.message
      };
    }
  };

  // Run all tests
  const runTests = async () => {
    setIsRunning(true);
    setResults([]);
    setTokenCache(new Map());
    setLoginErrors([]);

    const categoriesToTest = TEST_CATEGORIES.filter(c => selectedCategories.has(c.category));
    const rolesToTest = Array.from(selectedRoles);

    let totalTests = 0;
    categoriesToTest.forEach(c => totalTests += c.tests.length * rolesToTest.length);
    setProgress({ current: 0, total: totalTests, currentTest: '' });

    const categoryResults: CategoryResults[] = [];
    let testCount = 0;

    for (const category of categoriesToTest) {
      const results: TestResult[] = [];

      for (const test of category.tests) {
        for (const role of rolesToTest) {
          setProgress({
            current: testCount,
            total: totalTests,
            currentTest: `${test.method} ${test.path} (${role})`
          });

          const result = await testEndpoint(test, role);
          results.push(result);
          testCount++;
        }
      }

      categoryResults.push({
        category: category.category,
        results,
        passed: results.filter(r => r.passed).length,
        failed: results.filter(r => !r.passed).length
      });

      setResults([...categoryResults]);
    }

    setIsRunning(false);
  };

  // Export results as JSON
  const exportResults = () => {
    const data = JSON.stringify(results, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `api-test-results-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  // Export results as HTML report
  const exportHtmlReport = () => {
    const totalPassed = results.reduce((sum, c) => sum + c.passed, 0);
    const totalFailed = results.reduce((sum, c) => sum + c.failed, 0);
    const totalTests = totalPassed + totalFailed;
    const passRate = totalTests > 0 ? Math.round((totalPassed / totalTests) * 100) : 0;

    const html = `<!DOCTYPE html>
<html>
<head>
  <title>API Permission Test Report - ${new Date().toLocaleDateString()}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #f5f5f5; padding: 20px; }
    .container { max-width: 1200px; margin: 0 auto; }
    h1 { color: #1a1a1a; margin-bottom: 10px; }
    .subtitle { color: #666; margin-bottom: 30px; }
    .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 30px; }
    .card { background: white; border-radius: 8px; padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    .card .value { font-size: 32px; font-weight: bold; }
    .card .label { color: #666; font-size: 14px; }
    .card.passed .value { color: #22c55e; }
    .card.failed .value { color: #ef4444; }
    .card.rate .value { color: #3b82f6; }
    .category { background: white; border-radius: 8px; margin-bottom: 20px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    .category-header { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; background: #f8f9fa; border-bottom: 1px solid #e5e7eb; }
    .category-header h2 { font-size: 18px; }
    .badge { display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 500; }
    .badge.pass { background: #dcfce7; color: #166534; }
    .badge.fail { background: #fee2e2; color: #991b1b; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 12px 15px; text-align: left; border-bottom: 1px solid #e5e7eb; }
    th { background: #f8f9fa; font-weight: 600; font-size: 12px; text-transform: uppercase; color: #666; }
    tr:last-child td { border-bottom: none; }
    tr.fail-row { background: #fef2f2; }
    .method { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; color: white; }
    .method.GET { background: #22c55e; }
    .method.POST { background: #3b82f6; }
    .method.PUT { background: #eab308; }
    .method.DELETE { background: #ef4444; }
    .status { font-weight: 500; }
    .status.pass { color: #22c55e; }
    .status.fail { color: #ef4444; }
    .endpoint { font-family: monospace; font-size: 13px; }
    .role { font-family: monospace; font-size: 12px; background: #f3f4f6; padding: 2px 6px; border-radius: 3px; }
  </style>
</head>
<body>
  <div class="container">
    <h1>API Permission Test Report</h1>
    <p class="subtitle">Generated on ${new Date().toLocaleString()}</p>

    <div class="summary">
      <div class="card">
        <div class="value">${totalTests}</div>
        <div class="label">Total Tests</div>
      </div>
      <div class="card passed">
        <div class="value">${totalPassed}</div>
        <div class="label">Passed</div>
      </div>
      <div class="card failed">
        <div class="value">${totalFailed}</div>
        <div class="label">Failed</div>
      </div>
      <div class="card rate">
        <div class="value">${passRate}%</div>
        <div class="label">Pass Rate</div>
      </div>
    </div>

    ${results.map(category => `
    <div class="category">
      <div class="category-header">
        <h2>${category.category}</h2>
        <div>
          <span class="badge pass">${category.passed} passed</span>
          <span class="badge fail">${category.failed} failed</span>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Endpoint</th>
            <th>Role</th>
            <th>Expected</th>
            <th>Status</th>
            <th>Time</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          ${category.results.map(r => `
          <tr class="${r.passed ? '' : 'fail-row'}">
            <td><span class="method ${r.method}">${r.method}</span> <span class="endpoint">${r.endpoint.replace(`${r.method} `, '')}</span></td>
            <td><span class="role">${r.role}</span></td>
            <td>${r.expectedAccess ? 'Allowed' : 'Denied'}</td>
            <td>${r.actualStatus || 'Error'}</td>
            <td>${r.responseTime}ms</td>
            <td class="status ${r.passed ? 'pass' : 'fail'}">${r.passed ? 'PASS' : 'FAIL'}</td>
          </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
    `).join('')}
  </div>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `api-test-report-${new Date().toISOString().split('T')[0]}.html`;
    a.click();
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const totalPassed = results.reduce((sum, c) => sum + c.passed, 0);
  const totalFailed = results.reduce((sum, c) => sum + c.failed, 0);
  const totalTests = totalPassed + totalFailed;

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      GET: 'bg-green-500',
      POST: 'bg-blue-500',
      PUT: 'bg-yellow-500',
      PATCH: 'bg-orange-500',
      DELETE: 'bg-red-500'
    };
    return colors[method] || 'bg-gray-500';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">API Permission Tester</h1>
          <p className="text-gray-600 dark:text-gray-400">Automated testing of API endpoints with different user roles</p>
        </div>
        <button
          onClick={() => setShowCredentialsPanel(!showCredentialsPanel)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
            showCredentialsPanel
              ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300'
              : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300'
          }`}
        >
          <Settings className="w-4 h-4" />
          Configure Test Users
        </button>
      </div>

      {/* Credentials Configuration Panel */}
      {showCredentialsPanel && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 overflow-hidden">
          <div className="p-4 border-b bg-gray-50 dark:bg-gray-700 flex justify-between items-center">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Test User Credentials</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Update credentials for each role to match your database users</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={saveCredentials}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700"
              >
                <Save className="w-4 h-4" />
                Save to Browser
              </button>
              <button
                onClick={resetCredentials}
                className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-600 dark:text-gray-300"
              >
                <RotateCcw className="w-4 h-4" />
                Reset to Defaults
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-x-auto overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Role</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Username</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Password</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Action</th>
                </tr>
              </thead>
              <tbody>
                {ALL_ROLES.map((role) => (
                  <tr key={role} className="border-t dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-2">
                      <div className="font-mono text-xs font-semibold text-gray-700 dark:text-gray-300">{role}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{testUsers[role].description}</div>
                    </td>
                    <td className="px-4 py-2">
                      {editingRole === role ? (
                        <input
                          type="text"
                          value={editForm.username}
                          onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                          className="w-full px-2 py-1 border dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          autoFocus
                        />
                      ) : (
                        <span className="font-mono text-xs dark:text-gray-300">{testUsers[role].username}</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {editingRole === role ? (
                        <input
                          type="text"
                          value={editForm.password}
                          onChange={(e) => setEditForm(prev => ({ ...prev, password: e.target.value }))}
                          className="w-full px-2 py-1 border dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                      ) : (
                        <span className="font-mono text-xs text-gray-500 dark:text-gray-400">••••••••</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {editingRole === role ? (
                        <div className="flex gap-1">
                          <button
                            onClick={saveEditing}
                            className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs hover:bg-gray-50 dark:hover:bg-gray-600 dark:text-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditing(role)}
                          className="flex items-center gap-1 px-2 py-1 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded text-xs"
                        >
                          <Edit3 className="w-3 h-3" />
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border-t border-yellow-100 dark:border-yellow-800 text-xs text-yellow-800 dark:text-yellow-200">
            <strong>Note:</strong> Credentials are saved in your browser's local storage. Make sure to save changes before running tests.
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Run Button */}
          <button
            onClick={runTests}
            disabled={isRunning || selectedRoles.size === 0 || selectedCategories.size === 0}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium ${
              isRunning
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isRunning ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Running Tests...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Run Tests
              </>
            )}
          </button>

          {/* Role Selector */}
          <div className="relative">
            <button
              onClick={() => setShowRoleSelector(!showRoleSelector)}
              className="flex items-center gap-2 px-4 py-2.5 border dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
            >
              <User className="w-4 h-4" />
              Roles ({selectedRoles.size})
              <ChevronDown className="w-4 h-4" />
            </button>
            {showRoleSelector && (
              <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-lg shadow-lg z-10 w-64 max-h-80 overflow-y-auto">
                <div className="p-2 border-b dark:border-gray-600">
                  <button
                    onClick={() => setSelectedRoles(new Set(ALL_ROLES))}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline mr-3"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedRoles(new Set(QUICK_TEST_ROLES))}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline mr-3"
                  >
                    Quick Test
                  </button>
                  <button
                    onClick={() => setSelectedRoles(new Set())}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Clear
                  </button>
                </div>
                {ALL_ROLES.map(role => (
                  <label key={role} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedRoles.has(role)}
                      onChange={e => {
                        const next = new Set(selectedRoles);
                        if (e.target.checked) next.add(role);
                        else next.delete(role);
                        setSelectedRoles(next);
                      }}
                      className="rounded dark:bg-gray-700 dark:border-gray-500"
                    />
                    <span className="text-sm dark:text-gray-300">{role}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Category Selector */}
          <div className="relative">
            <button
              onClick={() => setShowCategorySelector(!showCategorySelector)}
              className="flex items-center gap-2 px-4 py-2.5 border dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300"
            >
              <Filter className="w-4 h-4" />
              Categories ({selectedCategories.size})
              <ChevronDown className="w-4 h-4" />
            </button>
            {showCategorySelector && (
              <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border dark:border-gray-600 rounded-lg shadow-lg z-10 w-48">
                <div className="p-2 border-b dark:border-gray-600">
                  <button
                    onClick={() => setSelectedCategories(new Set(TEST_CATEGORIES.map(c => c.category)))}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline mr-3"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedCategories(new Set())}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Clear
                  </button>
                </div>
                {TEST_CATEGORIES.map(c => (
                  <label key={c.category} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCategories.has(c.category)}
                      onChange={e => {
                        const next = new Set(selectedCategories);
                        if (e.target.checked) next.add(c.category);
                        else next.delete(c.category);
                        setSelectedCategories(next);
                      }}
                      className="rounded dark:bg-gray-700 dark:border-gray-500"
                    />
                    <span className="text-sm dark:text-gray-300">{c.category}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Export Buttons */}
          {results.length > 0 && (
            <>
              <button
                onClick={exportResults}
                className="flex items-center gap-2 px-4 py-2.5 border rounded-lg hover:bg-gray-50"
              >
                <Download className="w-4 h-4" />
                Export JSON
              </button>
              <button
                onClick={exportHtmlReport}
                className="flex items-center gap-2 px-4 py-2.5 border rounded-lg hover:bg-gray-50 text-blue-600 border-blue-200"
              >
                <Download className="w-4 h-4" />
                Export HTML Report
              </button>
            </>
          )}
        </div>

        {/* Progress */}
        {isRunning && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>{progress.currentTest}</span>
              <span>{progress.current} / {progress.total}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 rounded-full h-2 transition-all"
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Login Errors */}
      {loginErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <h3 className="text-red-800 font-semibold mb-2 flex items-center gap-2">
            <XCircle className="w-5 h-5" />
            Login Failures ({loginErrors.length} users)
          </h3>
          <p className="text-red-600 text-sm mb-3">
            The following test users could not log in. Make sure they exist in your database:
          </p>
          <div className="space-y-2">
            {loginErrors.map((err, idx) => (
              <div key={idx} className="bg-white rounded p-3 border border-red-100">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="font-mono text-sm font-semibold text-red-700">{err.role}</span>
                    <span className="text-gray-500 text-sm ml-2">({err.username})</span>
                  </div>
                </div>
                <div className="text-red-600 text-xs mt-1">{err.error}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      {results.length > 0 && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-3xl font-bold text-gray-900">{totalTests}</div>
            <div className="text-sm text-gray-500">Total Tests</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-3xl font-bold text-green-600">{totalPassed}</div>
            <div className="text-sm text-gray-500">Passed</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-3xl font-bold text-red-600">{totalFailed}</div>
            <div className="text-sm text-gray-500">Failed</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-3xl font-bold text-blue-600">
              {totalTests > 0 ? Math.round((totalPassed / totalTests) * 100) : 0}%
            </div>
            <div className="text-sm text-gray-500">Pass Rate</div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="space-y-4">
        {results.map((category) => (
          <div key={category.category} className="bg-white rounded-lg shadow overflow-hidden">
            <button
              onClick={() => toggleCategory(category.category)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                {expandedCategories.has(category.category) ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
                <span className="font-semibold">{category.category}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  {category.passed}
                </span>
                <span className="flex items-center gap-1 text-red-600">
                  <XCircle className="w-4 h-4" />
                  {category.failed}
                </span>
              </div>
            </button>

            {expandedCategories.has(category.category) && (
              <div className="border-t overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Endpoint</th>
                      <th className="px-4 py-2 text-left">Role</th>
                      <th className="px-4 py-2 text-left">Expected</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Time</th>
                      <th className="px-4 py-2 text-left">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {category.results.map((result, idx) => (
                      <tr key={idx} className={`border-t ${result.passed ? '' : 'bg-red-50'}`}>
                        <td className="px-4 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs text-white mr-2 ${getMethodColor(result.method)}`}>
                            {result.method}
                          </span>
                          <span className="font-mono text-xs">{result.endpoint.replace(`${result.method} `, '')}</span>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{result.role}</td>
                        <td className="px-4 py-2">
                          {result.expectedAccess ? (
                            <span className="text-green-600">Allowed</span>
                          ) : (
                            <span className="text-red-600">Denied</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {result.actualStatus || 'Error'}
                          {result.error && (
                            <div className="text-xs text-red-500 mt-1 max-w-xs truncate" title={result.error}>
                              {result.error}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-500">{result.responseTime}ms</td>
                        <td className="px-4 py-2">
                          {result.passed ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {results.length === 0 && !isRunning && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Play className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Test</h3>
          <p className="text-gray-500 mb-4">
            Select roles and categories, then click "Run Tests" to start automated API permission testing.
          </p>
          <p className="text-sm text-gray-400">
            Make sure test users exist in your database with correct credentials.
          </p>
        </div>
      )}
    </div>
  );
};

export default ApiTesterPage;
